import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "@/Components/Common/BreadCrumb";
import DraggableTable, { DraggableTableColumn } from "@/Components/Common/DraggableTable";
import { Button, Card, CardBody, CardHeader, Container, Input, Label } from "@/Components/ui";
import { Menu, MenuCategory, tableOrderService } from "@/api/services/table-order.service";
import { dialogService } from "@/utils/dialogService";
import { toast } from "react-hot-toast";

const mergeVisibleMenuOrder = (allMenus: Menu[], reorderedVisibleMenus: Menu[]): Menu[] => {
  const reorderedIds = new Set(reorderedVisibleMenus.map((menu) => menu.id));
  let reorderedIndex = 0;

  return allMenus
    .map((menu) => {
      if (!reorderedIds.has(menu.id)) {
        return menu;
      }

      const nextMenu = reorderedVisibleMenus[reorderedIndex];
      reorderedIndex += 1;
      return nextMenu;
    })
    .map((menu, index) => ({
      ...menu,
      display_order: index,
    }));
};

const fulfillmentTypeLabels: Record<string, string> = {
  INSTANT: "즉시 제공",
  KITCHEN: "제조 필요",
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  return fallbackMessage;
};

type MenuToggleField =
  | "is_active"
  | "is_web_visible"
  | "is_pos_visible"
  | "is_kiosk_visible"
  | "is_sold_out";

const menuToggleMeta: Record<
  MenuToggleField,
  {
    enabledLabel: string;
    disabledLabel: string;
    enabledColor: string;
  }
> = {
  is_active: {
    enabledLabel: "활성",
    disabledLabel: "비활성",
    enabledColor: "soft-success",
  },
  is_web_visible: {
    enabledLabel: "노출",
    disabledLabel: "숨김",
    enabledColor: "soft-primary",
  },
  is_pos_visible: {
    enabledLabel: "노출",
    disabledLabel: "숨김",
    enabledColor: "soft-info",
  },
  is_kiosk_visible: {
    enabledLabel: "노출",
    disabledLabel: "숨김",
    enabledColor: "soft-warning",
  },
  is_sold_out: {
    enabledLabel: "품절",
    disabledLabel: "판매중",
    enabledColor: "soft-danger",
  },
};

const Menus = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [keyword, setKeyword] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [categoryRes, menuRes] = await Promise.all([
        tableOrderService.listAllMenuCategories(),
        tableOrderService.listAllMenus(),
      ]);
      setCategories(categoryRes.results || []);
      setMenus(menuRes.results || []);
    } catch (error) {
      toast.error("메뉴 데이터를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const categoryNameById = useMemo(() => {
    const map: Record<number, string> = {};
    categories.forEach((category) => {
      map[category.id] = category.name;
    });
    return map;
  }, [categories]);

  const filteredMenus = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return menus.filter((menu) => {
      if (categoryFilter && String(menu.category) !== categoryFilter) return false;
      if (normalizedKeyword && !menu.name.toLowerCase().includes(normalizedKeyword)) return false;
      return true;
    });
  }, [menus, categoryFilter, keyword]);

  const handleVisibleMenusReorder = useCallback((reorderedVisibleMenus: Menu[]) => {
    setMenus((prevMenus) => mergeVisibleMenuOrder(prevMenus, reorderedVisibleMenus));
  }, []);

  const handleSaveOrder = useCallback(async () => {
    try {
      await tableOrderService.reorderMenus(menus.map((menu) => menu.id));
      await loadAll();
      toast.success("메뉴 순서가 저장되었습니다.");
    } catch (error) {
      await loadAll();
      throw error;
    }
  }, [loadAll, menus]);

  const handleToggleMenuFlag = useCallback(async (menu: Menu, field: MenuToggleField) => {
    try {
      await tableOrderService.updateMenu(menu.id, { [field]: !menu[field] });
      await loadAll();
      toast.success("메뉴 상태가 변경되었습니다.");
    } catch (error) {
      toast.error("메뉴 상태 변경에 실패했습니다.");
    }
  }, [loadAll]);

  const handleDeleteMenu = useCallback(async (menu: Menu) => {
    if (!await dialogService.confirm(`'${menu.name}' 메뉴를 삭제하시겠습니까?`)) return;

    try {
      await tableOrderService.deleteMenu(menu.id);
      await loadAll();
      toast.success("메뉴가 삭제되었습니다.");
    } catch (error) {
      toast.error("메뉴 삭제에 실패했습니다.");
    }
  }, [loadAll]);

  const columns = useMemo<DraggableTableColumn<Menu>[]>(() => [
    {
      key: "id",
      header: "ID",
      className: "whitespace-nowrap",
      render: (menu) => menu.id,
    },
    {
      key: "image",
      header: "이미지",
      className: "whitespace-nowrap",
      render: (menu) => menu.image ? (
        <img
          src={menu.image}
          alt={menu.name}
          className="rounded border border-slate-200"
          style={{ width: 56, height: 56, objectFit: "cover" }}
        />
      ) : (
        <span className="text-muted">없음</span>
      ),
    },
    {
      key: "category",
      header: "카테고리",
      className: "whitespace-nowrap",
      render: (menu) => categoryNameById[menu.category] || `#${menu.category}`,
    },
    {
      key: "name",
      header: "메뉴명",
      className: "min-w-[240px]",
      render: (menu) => (
        <div>
          <div className="fw-semibold">{menu.name}</div>
          <small className="text-muted">{menu.description || "설명 없음"}</small>
        </div>
      ),
    },
    {
      key: "price",
      header: "가격",
      className: "whitespace-nowrap",
      render: (menu) => `${menu.price.toLocaleString()}원`,
    },
    {
      key: "fulfillment_type",
      header: "제공 타입",
      className: "whitespace-nowrap",
      render: (menu) => fulfillmentTypeLabels[menu.fulfillment_type] || menu.fulfillment_type,
    },
    {
      key: "display_order",
      header: "정렬",
      className: "whitespace-nowrap",
      render: (menu) => menu.display_order,
    },
    {
      key: "active",
      header: "활성",
      className: "whitespace-nowrap",
      render: (menu) => (
        <Button
          color={menu.is_active ? menuToggleMeta.is_active.enabledColor : "soft-secondary"}
          size="sm"
          onClick={() => void handleToggleMenuFlag(menu, "is_active")}
        >
          {menu.is_active ? menuToggleMeta.is_active.enabledLabel : menuToggleMeta.is_active.disabledLabel}
        </Button>
      ),
    },
    {
      key: "web_visibility",
      header: "웹 노출",
      className: "whitespace-nowrap",
      render: (menu) => (
        <Button
          color={menu.is_web_visible ? menuToggleMeta.is_web_visible.enabledColor : "soft-secondary"}
          size="sm"
          onClick={() => void handleToggleMenuFlag(menu, "is_web_visible")}
        >
          {menu.is_web_visible ? menuToggleMeta.is_web_visible.enabledLabel : menuToggleMeta.is_web_visible.disabledLabel}
        </Button>
      ),
    },
    {
      key: "pos_visibility",
      header: "POS 노출",
      className: "whitespace-nowrap",
      render: (menu) => (
        <Button
          color={menu.is_pos_visible ? menuToggleMeta.is_pos_visible.enabledColor : "soft-secondary"}
          size="sm"
          onClick={() => void handleToggleMenuFlag(menu, "is_pos_visible")}
        >
          {menu.is_pos_visible ? menuToggleMeta.is_pos_visible.enabledLabel : menuToggleMeta.is_pos_visible.disabledLabel}
        </Button>
      ),
    },
    {
      key: "kiosk_visibility",
      header: "키오스크 노출",
      className: "whitespace-nowrap",
      render: (menu) => (
        <Button
          color={menu.is_kiosk_visible ? menuToggleMeta.is_kiosk_visible.enabledColor : "soft-secondary"}
          size="sm"
          onClick={() => void handleToggleMenuFlag(menu, "is_kiosk_visible")}
        >
          {menu.is_kiosk_visible ? menuToggleMeta.is_kiosk_visible.enabledLabel : menuToggleMeta.is_kiosk_visible.disabledLabel}
        </Button>
      ),
    },
    {
      key: "sold_out",
      header: "판매상태",
      className: "whitespace-nowrap",
      render: (menu) => (
        <Button
          color={menu.is_sold_out ? menuToggleMeta.is_sold_out.enabledColor : "soft-info"}
          size="sm"
          onClick={() => void handleToggleMenuFlag(menu, "is_sold_out")}
        >
          {menu.is_sold_out ? menuToggleMeta.is_sold_out.enabledLabel : menuToggleMeta.is_sold_out.disabledLabel}
        </Button>
      ),
    },
    {
      key: "actions",
      header: "관리",
      className: "whitespace-nowrap",
      render: (menu) => (
        <div className="flex gap-2">
          <Button
            color="soft-primary"
            size="sm"
            onClick={() => navigate(`/table-order/menu-form/${menu.id}`)}
          >
            수정
          </Button>
          <Button
            color="soft-danger"
            size="sm"
            onClick={() => void handleDeleteMenu(menu)}
          >
            삭제
          </Button>
        </div>
      ),
    },
  ], [categoryNameById, handleDeleteMenu, handleToggleMenuFlag, navigate]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="메뉴 관리" pageTitle="현장주문" />

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">메뉴 목록</h5>
            <Button color="primary" onClick={() => navigate("/table-order/menu-form/0")}>
              추가
            </Button>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 gap-3 mb-3 md:grid-cols-3">
              <div>
                <Label className="form-label">카테고리</Label>
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">전체</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="form-label">검색</Label>
                <Input
                  value={keyword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
                  placeholder="메뉴명을 입력하세요"
                />
              </div>
            </div>

            {categoryFilter || keyword.trim() ? (
              <p className="mb-3 text-xs text-slate-500">
                현재 보이는 메뉴만 드래그해도 사이트 전체 순서에 반영되도록 병합 저장됩니다.
              </p>
            ) : null}

            {loading ? (
              <p className="text-muted mb-0">불러오는 중...</p>
            ) : (
              <DraggableTable
                data={filteredMenus}
                getRowId={(menu) => menu.id}
                columns={columns}
                onReorder={handleVisibleMenusReorder}
                onSaveOrder={filteredMenus.length > 0 ? handleSaveOrder : undefined}
                onSaveError={(error) => {
                  toast.error(getErrorMessage(error, "메뉴 순서 저장에 실패했습니다."));
                }}
                saveLabel="순서 저장"
                savingLabel="저장 중..."
                emptyText="등록된 메뉴가 없습니다."
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Menus;
